import { Box, Stack, Typography } from '@mui/material';
import CalendarIcon from '../CustomSVGIcons/CalendarIcon';
import { useThemeContext } from '../../hook/useThemeManager';
import { theme } from '../../constants/theme';
import { FONT_ENUM } from '../../types/global/fonts.types';
import dayjs from 'dayjs';
import CloseIconWithBG from '../CustomSVGIcons/CloseIconWithBG';

export type TPhase = 'submission' | 'ideation' | 'pitch' | 'production' | 'judging' | 'vote' | 'none' | 'event';
export type PhaseData = {
    phase: TPhase;
    date_started: string;
    expiration_date: string;
};
export interface IContestPhaseTab {
    data: PhaseData;
    onRemovePhase?: (data: PhaseData, index?: number) => void;
    onEditPhase?: (data: PhaseData, index: number) => void;
    index: number;
    readOnly?: boolean;
}

const ContestPhaseTab: React.FC<IContestPhaseTab> = ({ data, onRemovePhase, onEditPhase, index, readOnly }) => {
    const { themeStyle } = useThemeContext();

    const handleClick = () => {
        if (!readOnly && onEditPhase) {
            onEditPhase(data, index);
        }
    };

    return (
        <Stack
            width="fit-content"
            direction="row"
            height="fit-content"
            alignItems="center"
            border={`0.2px solid ${theme[themeStyle].darkGray60}`}
            padding={2}
            position="relative"
            bgcolor={theme[themeStyle].deep_grey_lighter}
            mr={1.2}
            mb={1.2}
            borderRadius={2}
            onClick={handleClick}
            sx={{
                cursor: readOnly ? 'default' : 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': readOnly
                    ? {}
                    : {
                          borderColor: theme[themeStyle].primary_orange,
                          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      },
            }}>
            {!readOnly && (
                <Box
                    onClick={e => {
                        e.stopPropagation();
                        onRemovePhase && onRemovePhase(data, index);
                    }}
                    sx={{
                        position: 'absolute',
                        right: -5,
                        top: -5,
                        cursor: 'pointer',
                        zIndex: 1,
                    }}>
                    <CloseIconWithBG height={'20px'} width={'20px'} />
                </Box>
            )}
            <CalendarIcon
                height={50}
                width={50}
                stroke={theme[themeStyle].tertiary_dark}
                fill={theme[themeStyle].tertiary_dark}
                style={{
                    margin: '10px',
                }}
            />
            <Box>
                <Typography
                    sx={{
                        color: theme[themeStyle].highlight,
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        fontSize: 8,
                    }}>
                    Phase
                </Typography>
                <Typography
                    sx={{
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        fontSize: 12,
                        textTransform: 'capitalize',
                    }}>
                    {data.phase}
                </Typography>
                <Typography
                    sx={{
                        color: theme[themeStyle].highlight,
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        fontSize: 8,
                    }}>
                    Start Date
                </Typography>
                <Typography
                    sx={{
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        fontSize: 12,
                        textTransform: 'capitalize',
                    }}>
                    {dayjs(data.date_started).format('MMM. D, YYYY h:mm A')}
                </Typography>
                <Typography
                    sx={{
                        color: theme[themeStyle].highlight,
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        fontSize: 8,
                    }}>
                    End Date
                </Typography>
                <Typography
                    sx={{
                        color: theme[themeStyle].tag_white,
                        fontFamily: FONT_ENUM.REGULAR_HEADER,
                        fontSize: 12,
                        textTransform: 'capitalize',
                    }}>
                    {dayjs(data.expiration_date).format('MMM. D, YYYY h:mm A')}
                </Typography>
            </Box>
        </Stack>
    );
};

ContestPhaseTab.defaultProps = {
    data: {
        phase: 'production',
        date_started: '2024-01-23T12:34:56Z',
        expiration_date: '2024-03-23T12:34:56Z',
    },
};

export { ContestPhaseTab };
